import React, { useEffect, useState } from "react";
import { getArtistOutsourcePersons, createOutsourcePerson, updateOutsourcePerson, deleteOutsourcePerson } from "../../services/outsourcePersonService";
import api from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

interface OutsourcePerson {
  id: number;
  name: string;
  roleId: number;
  role?: { id: number; roleName: string };
  contact: string;
  email?: string;
  notes?: string;
}

interface OutsourceRole {
  id: number;
  roleName: string;
}

export default function ArtistOutsourcePersons() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [persons, setPersons] = useState<OutsourcePerson[]>([]);
  const [roles, setRoles] = useState<OutsourceRole[]>([]);
  const [form, setForm] = useState<Partial<OutsourcePerson>>({});
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [unauthorized, setUnauthorized] = useState(false);

  const loadPersons = async () => {
    try {
      setLoading(true);
      const res = await getArtistOutsourcePersons();
      const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : []);
      setPersons(list);
    } catch (err: any) {
      // Do not auto-redirect on 401/403; show inline error to avoid feeling like a force logout
      const status = err?.response?.status;
      const msg = status === 401
        ? "Unauthorized: please ensure your session is valid."
        : status === 403
        ? "Access denied: you don't have permission to view outsource persons."
        : (err?.response?.data?.message || err?.message || JSON.stringify(err));
      setError("Failed to load persons. " + msg);
      setUnauthorized(status === 401);
      console.error("Load persons error:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
  const res = await api.get<OutsourceRole[] | { items: OutsourceRole[] }>("/OutsourceRole");
  const data: any = res.data;
  const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  setRoles(list);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = status === 401
        ? "Unauthorized: please ensure your session is valid."
        : status === 403
        ? "Access denied: you don't have permission to view roles."
        : "Failed to load roles.";
      setError(msg);
      if (status === 401) setUnauthorized(true);
    }
  };

  useEffect(() => {
    loadPersons();
    loadRoles();
  }, []);

  const handleSave = async () => {
    if (!form.name || !form.roleId) {
      setError("Name and Role are required.");
      return;
    }
    // Ensure we associate the new outsource person with the logged-in artist
    const artistIdStr = (typeof window !== 'undefined') ? localStorage.getItem('makeupArtistId') : null;
    const makeupArtistId = artistIdStr ? Number(artistIdStr) : 0;
    if (!editingId && (!makeupArtistId || Number.isNaN(makeupArtistId))) {
      setError("Missing makeup artist identity. Please re-login and try again.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload: any = {
        name: form.name,
        contact: form.contact || "",
        email: form.email || "",
        roleId: form.roleId,
        notes: form.notes || "",
        isActive: true,
        // Include makeupArtistId on create to avoid defaulting to artist id 1 on backend
        ...(editingId ? {} : { makeupArtistId }),
      };
      if (editingId) {
        await updateOutsourcePerson(editingId, payload);
      } else {
        await createOutsourcePerson(payload);
      }
      setForm({});
      setEditingId(null);
      loadPersons();
    } catch (err: any) {
      setError("Failed to save person. " + (err?.response?.data?.message || err?.message || JSON.stringify(err)));
      console.error("Save person error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (person: OutsourcePerson) => {
    setForm({
      name: person.name,
      roleId: person.roleId,
      contact: person.contact,
      email: person.email || "",
      notes: person.notes || "",
    });
    setEditingId(person.id);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this outsource person?")) return;
    setLoading(true);
    try {
      await deleteOutsourcePerson(id);
      loadPersons();
    } catch (err: any) {
      setError("Failed to delete person. " + (err?.response?.data?.message || err?.message || JSON.stringify(err)));
      console.error("Delete person error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">My Outsource Persons</h2>
      {unauthorized && (
        <div className="mb-4 p-3 rounded bg-yellow-50 text-yellow-800 flex items-center justify-between">
          <span>Your session may have expired. Please re-login to continue.</span>
          <div className="space-x-2">
            <button
              className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
              onClick={() => {
                setUnauthorized(false);
                setError("");
                loadPersons();
              }}
            >Retry</button>
            <button
              className="px-3 py-1 rounded bg-pink-600 text-white hover:bg-pink-700"
              onClick={() => {
                logout();
                navigate('/login');
              }}
            >Re-login</button>
          </div>
        </div>
      )}
      <div className="mb-6 bg-gray-50 p-4 rounded shadow">
        <h3 className="font-semibold mb-2">{editingId ? "Edit" : "Add"} Outsource Person</h3>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <input
            type="text"
            placeholder="Name"
            value={form.name || ""}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="border rounded px-3 py-2"
          />
          <select
            value={form.roleId || ""}
            onChange={e => setForm(f => ({ ...f, roleId: Number(e.target.value) }))}
            className="border rounded px-3 py-2"
          >
            <option value="">Select Role</option>
            {roles.map(r => (
              <option key={r.id} value={r.id}>{r.roleName}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Contact"
            value={form.contact || ""}
            onChange={e => setForm(f => ({ ...f, contact: e.target.value }))}
            className="border rounded px-3 py-2"
          />
          <input
            type="email"
            placeholder="Email"
            value={form.email || ""}
            onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
            className="border rounded px-3 py-2"
          />
          <input
            type="text"
            placeholder="Notes"
            value={form.notes || ""}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            className="border rounded px-3 py-2"
          />
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={handleSave}
            disabled={loading}
            className="btn-primary px-4 py-2"
          >
            {editingId ? "Update" : "Add"}
          </button>
          {editingId && (
            <button
              onClick={() => { setForm({}); setEditingId(null); }}
              className="bg-gray-300 px-4 py-2 rounded"
            >Cancel</button>
          )}
        </div>
        {error && <p className="text-red-500 mt-2">{error}</p>}
      </div>

      <div className="overflow-x-auto bg-white rounded shadow">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Role</th>
              <th className="px-4 py-2 text-left">Contact</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Notes</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {persons.map(p => (
              <tr key={p.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2">{p.role?.roleName || '-'}</td>
                <td className="px-4 py-2">{p.contact}</td>
                <td className="px-4 py-2">{p.email || '-'}</td>
                <td className="px-4 py-2">{p.notes || '-'}</td>
                <td className="px-4 py-2 text-right">
                  <button onClick={() => handleEdit(p)} className="text-blue-600 hover:text-blue-800 mr-2">Edit</button>
                  <button onClick={() => handleDelete(p.id)} className="text-red-600 hover:text-red-800">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
